/**
 * Authentication Modal Component
 * Handles email link sign-in flow
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, CheckCircle, Loader2, Zap, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { trackEvent } from '@/lib/analytics';
import { friendlyAuthError } from '@/lib/auth-errors';

// Google "G" logo SVG component
function GoogleIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

export default function AuthModal({ isOpen, onClose, onSuccess }) {
  const { sendSignInLink, signInWithGoogle } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState('');

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError('');

    const result = await signInWithGoogle();

    setGoogleLoading(false);

    if (result.success) {
      trackEvent('signup_completed', { method: 'google', source: 'auth_modal' });
      onSuccess?.();
      handleClose();
    } else {
      const friendly = friendlyAuthError(result.code);
      if (friendly === null) {
        // User closed the popup themselves — not an error, show nothing
        trackEvent('signup_google_cancelled');
        return;
      }
      setError(friendly);
      trackEvent('signup_google_error', { code: result.code });
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');

    const result = await sendSignInLink(email);

    setLoading(false);

    if (result.success) {
      setResent(sent);
      setSent(true);
      trackEvent('signup_link_sent', { source: 'auth_modal' });
    } else {
      setError(friendlyAuthError(result.code));
      trackEvent('signup_link_error', { code: result.code });
    }
  };

  const handleUseDifferentEmail = () => {
    setSent(false);
    setResent(false);
    setError('');
  };

  const handleClose = useCallback(() => {
    setEmail('');
    setSent(false);
    setResent(false);
    setError('');
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex min-h-screen items-center justify-center overflow-y-auto bg-black/50 backdrop-blur-sm p-6 md:p-10"
        onClick={handleClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-modal-title"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md"
        >
          <Card className="rounded-3xl border border-[rgba(31,76,84,0.12)] shadow-[0_24px_48px_rgba(15,23,42,0.12)]">
            <CardHeader className="space-y-1">
              <div className="flex items-center justify-between">
                <CardTitle id="auth-modal-title" className="text-2xl font-semibold text-[var(--text-primary)]">
                  {sent ? 'Check your email' : 'Get 10 free analyses'}
                </CardTitle>
                <button
                  onClick={handleClose}
                  className="rounded-full border border-[rgba(31,76,84,0.12)] p-1 text-[var(--text-soft)] transition hover:text-[var(--text-primary)]"
                  aria-label="Close dialog"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
              <CardDescription>
                {sent 
                  ? 'We sent you a secure sign-in link.'
                  : 'No password required—just your email.'}
              </CardDescription>
            </CardHeader>

            <CardContent>
              {!sent ? (
                <>
                  {/* Error surface for both sign-in methods */}
                  <div aria-live="polite">
                    {error && (
                      <Alert variant="danger" className="mb-4">
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}
                  </div>

                  {/* Benefits */}
                  <div className="mb-6 space-y-3">
                    <div className="flex items-center gap-3 text-sm text-[var(--text-secondary)]">
                      <Zap className="h-5 w-5 text-[var(--accent)]" />
                      <span>Ten complimentary analyses per month.</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-[var(--text-secondary)]">
                      <CheckCircle className="h-5 w-5 text-success-700" />
                      <span>Export executive-ready briefings as PDF.</span>
                    </div>
                  </div>

                  {/* Google Sign-in Button */}
                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={googleLoading}
                    className="mb-4 flex w-full items-center justify-center gap-3 rounded-2xl border border-[rgba(31,76,84,0.16)] bg-white px-4 py-3 text-sm font-medium text-[var(--text-primary)] transition hover:bg-neutral-50 focus:ring-4 focus:ring-[rgba(31,76,84,0.12)] disabled:opacity-50"
                  >
                    {googleLoading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <GoogleIcon className="h-5 w-5" />
                    )}
                    {googleLoading ? 'Signing in...' : 'Continue with Google'}
                  </button>

                  {/* Divider */}
                  <div className="relative mb-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-[rgba(31,76,84,0.12)]" />
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="bg-white px-2 text-[var(--text-soft)]">or continue with email</span>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="email" className="mb-2 block text-sm font-medium text-[var(--text-primary)]">
                        Email address
                      </label>
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        required
                        autoFocus
                        autoComplete="email"
                        aria-required="true"
                        aria-invalid={error ? "true" : "false"}
                        className="w-full rounded-2xl border border-[rgba(31,76,84,0.16)] bg-white px-4 py-3 text-[var(--text-primary)] transition focus:border-[var(--accent)] focus:ring-4 focus:ring-[rgba(31,76,84,0.12)]"
                      />
                    </div>

                    <Button type="submit" disabled={loading} size="lg" className="w-full justify-center">
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Mail className="mr-2 h-4 w-4" />
                          Send Magic Link
                        </>
                      )}
                    </Button>
                  </form>

                  <p className="mt-4 text-center text-xs text-[var(--text-secondary)]">
                    By signing up, you agree to our privacy policy. Your data is encrypted and handled in line with GDPR.
                  </p>
                </>
              ) : (
                <div className="py-6 text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-[rgba(31,76,84,0.16)] bg-[var(--accent-light)]"
                  >
                    <CheckCircle className="h-8 w-8 text-success-700" />
                  </motion.div>

                  <h3 className="mb-2 text-lg font-semibold text-[var(--text-primary)]">
                    {resent ? 'Link sent again' : 'Email sent'}
                  </h3>
                  <p className="mb-4 text-sm text-[var(--text-secondary)]">
                    We sent a sign-in link to <span className="font-medium">{email}</span>.
                  </p>
                  <p className="mb-6 text-xs text-[var(--text-soft)]">
                    Click the link to complete sign-in. The link expires in one hour.
                    <br />
                    Can&apos;t find it? Check your spam or junk folder.
                  </p>

                  <div aria-live="polite">
                    {error && (
                      <Alert variant="danger" className="mb-4 text-left">
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}
                  </div>

                  <div className="flex items-center justify-center gap-3">
                    <Button variant="outline" onClick={handleSubmit} disabled={loading}>
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        'Send again'
                      )}
                    </Button>
                    <Button onClick={handleClose}>Done</Button>
                  </div>

                  <button
                    type="button"
                    onClick={handleUseDifferentEmail}
                    className="mt-4 text-xs text-[var(--text-soft)] underline underline-offset-2 transition hover:text-[var(--text-primary)]"
                  >
                    Use a different email address
                  </button>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
